import React, { useEffect, useState } from "react";
import { FormField, Input, TextArea } from "../../design-system/components/inputs";
import { apiFetch } from "../../core/utils/api";
import { APP_DOMAIN } from "../../core/utils/config";
import { typography, surfaces, textColors, brandColors, interactive } from "../../design-system/tokens";

/**
 * Shared feedback form used both by the standalone Feedback page and in-editor modal.
 */
const FeedbackForm = ({ showCard = true, onSubmitted, situation = "PUBLIC_FEEDBACK_PAGE" }) => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    rating: 0,
    feedback: "",
  });
  const [hoveredStar, setHoveredStar] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Check if user is logged in and get their profile
  useEffect(() => {
    let cancelled = false;
    const checkAuthAndFetchProfile = async () => {
      setIsCheckingAuth(true);
      try {
        const response = await apiFetch("/api/auth/profile/", {
          credentials: "include",
        });

        if (!response.ok) {
          if (!cancelled) setIsLoggedIn(false);
          return;
        }

        const userData = await response.json();
        if (cancelled) return;

        setIsLoggedIn(true);
        setFormData((prev) => ({
          ...prev,
          email: userData.email || "",
          name:
            `${userData.first_name || ""} ${userData.last_name || ""}`.trim() ||
            userData.username ||
            "",
        }));
      } catch (err) {
        if (!cancelled) setIsLoggedIn(false);
      } finally {
        if (!cancelled) setIsCheckingAuth(false);
      }
    };

    checkAuthAndFetchProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStarClick = (rating) => {
    setFormData((prev) => ({
      ...prev,
      rating,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoggedIn) {
      // For logged-in users, require either rating or feedback text
      if (!formData.rating && !formData.feedback.trim()) {
        setMessage("Please provide either a rating or feedback text.");
        setMessageType("error");
        return;
      }
    } else {
      // For non-logged-in users, require email
      if (!formData.email.trim()) {
        setMessage("Email is required to send feedback.");
        setMessageType("error");
        return;
      }
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      const payload = {
        ...formData,
        situation,
      };
      const response = await apiFetch("/api/feedback/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (response.ok) {
        setMessage(
          "Thank you for your feedback! We appreciate your input and will work hard to provide the best service we can."
        );
        setMessageType("success");

        if (isLoggedIn) {
          // For logged-in users, only reset rating and feedback
          setFormData((prev) => ({
            ...prev,
            rating: 0,
            feedback: "",
          }));
        } else {
          // For non-logged-in users, reset all fields
          setFormData({
            email: "",
            name: "",
            rating: 0,
            feedback: "",
          });
        }

        if (typeof onSubmitted === "function") onSubmitted();
      } else {
        const data = await response.json().catch(() => ({}));
        setMessage(data.error || "Failed to send feedback. Please try again.");
        setMessageType("error");
      }
    } catch (err) {
      setMessage("We were unable to process your feedback. Please try again later.");
      setMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleStarClick(star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(0)}
            className={`text-2xl transition-colors duration-150 focus:outline-none focus:ring-2 ${brandColors.focus} focus:ring-offset-1 rounded`}
          >
            <svg
              className={`w-8 h-8 ${
                star <= (hoveredStar || formData.rating)
                  ? "text-blue-500 dark:text-sky-400"
                  : "text-gray-200 dark:text-dark-700"
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        ))}
        <span className={`ml-2 text-sm ${textColors.secondary}`}>
          {formData.rating > 0
            ? `${formData.rating} star${formData.rating !== 1 ? "s" : ""}`
            : "No rating"}
        </span>
      </div>
    );
  };

  const wrapperClasses = showCard
    ? `${surfaces.primary} rounded-lg shadow-md p-8`
    : "p-0";

  if (isCheckingAuth) {
    return (
      <div className={wrapperClasses}>
        <div className="flex items-center justify-center py-8">
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-500`} />
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClasses}>
      <div className="mb-8 text-center">
        <h2 className={`${typography.h3} ${textColors.primary} mb-4`}>We'd Love to Hear From You</h2>
        <p className={`${typography.body} ${textColors.secondary}`}>
          Please share your thoughts, suggestions, or report any issues you've encountered.
        </p>
        {isLoggedIn && (
          <p className={`${typography.bodySmall} ${brandColors.primary} mt-2`}>
            Logged in as: {formData.name || formData.email}
          </p>
        )}
      </div>

      {/* Message Display */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-md ${typography.body} ${
            messageType === "success"
              ? "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800"
              : "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800"
          }`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Field - Only show if not logged in */}
        {!isLoggedIn && (
          <FormField label="Email Address" htmlFor="email" required>
            <Input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.email@example.com"
              required
            />
          </FormField>
        )}

        {/* Name Field - Only show if not logged in */}
        {!isLoggedIn && (
          <FormField label="Name (Optional)" htmlFor="name">
            <Input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Your full name"
            />
          </FormField>
        )}

        {/* Rating Field */}
        <div>
          <label className={`${typography.label} ${textColors.primary} mb-3 block text-center`}>
            Overall Rating (Optional)
          </label>
          <div className="flex items-center justify-center">{renderStars()}</div>
          <p className={`${typography.caption} ${textColors.muted} mt-2 text-center`}>
            Click on a star to rate your experience with {APP_DOMAIN}
          </p>
        </div>

        {/* Feedback Text */}
        <FormField label="Your Feedback (Optional)" htmlFor="feedback">
          <TextArea
            id="feedback"
            name="feedback"
            value={formData.feedback}
            onChange={handleInputChange}
            rows={6}
            placeholder="Please share your thoughts, suggestions, or report any issues you've encountered..."
          />
        </FormField>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={
              isSubmitting ||
              (!isLoggedIn && !formData.email.trim()) ||
              (isLoggedIn && !formData.rating && !formData.feedback.trim())
            }
            className={`w-full py-3 px-4 rounded-md ${typography.buttonLarge} transition-all duration-200 ${
              isSubmitting ||
              (!isLoggedIn && !formData.email.trim()) ||
              (isLoggedIn && !formData.rating && !formData.feedback.trim())
                ? "bg-gray-200 dark:bg-dark-800 text-gray-500 dark:text-dark-500 cursor-not-allowed opacity-50"
                : `${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText} shadow-md hover:shadow-lg transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2`
            }`}
          >
            {isSubmitting ? "Sending..." : "Send Feedback"}
          </button>
        </div>

        <div className="text-center">
          <p className={`${typography.bodySmall} ${textColors.muted}`}>
            {isLoggedIn
              ? "Please provide either a rating or feedback text to submit."
              : "* Required field. We'll never share your email address."}
          </p>
        </div>
      </form>
    </div>
  );
};

export default FeedbackForm;

